import { Card, CardContent } from "@repo/ui/components/ui/card";
import { Input } from "@repo/ui/components/ui/input";
import { cn } from "@repo/ui/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { recordHotkey, resolveHotkey } from "@/app/command-palette/hotkey-store";
import { allGroupedCommands, categoryLabels } from "@/app/command-palette/registry";
import { useHotkeyBindings } from "@/app/command-palette/use-hotkey-store";
import { HotkeyRecorderCell } from "@/app/settings/hotkey-recorder-cell";
import { SettingsNav } from "@/app/shell/settings-nav";

export const Route = createFileRoute("/settings/hotkeys")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: (search.q as string) || undefined,
  }),
});

function RouteComponent() {
  const { q: search = "" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSearch = (value: string) => navigate({ search: { q: value || undefined }, replace: true });
  const bindings = useHotkeyBindings();

  const filteredGroups = (Object.keys(categoryLabels) as Array<keyof typeof categoryLabels>)
    .map((cat) => {
      const filtered = allGroupedCommands[cat].filter((c) => c.label.toLowerCase().includes(search.toLowerCase()));
      return [cat, filtered] as const;
    })
    .filter(([, cmds]) => cmds.length > 0);

  return (
    <div className="py-12 space-y-6">
      <SettingsNav />
      <div className="max-w-(--form-max-width) mx-auto">
        <div className="flex flex-col gap-4">
          <Input
            placeholder="Search commands..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
          <Card className="py-0 gap-0">
            <CardContent className="p-0">
              <div>
                {filteredGroups.map(([cat, cmds], i) => (
                  <div key={cat}>
                    <div className={cn("px-6 py-2 bg-muted/50", i > 0 && "border-t")}>
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {categoryLabels[cat]}
                      </span>
                    </div>
                    {cmds.map((cmd) => (
                      <div key={cmd.id} className="flex items-center justify-between px-6 py-3 border-t">
                        <div className="flex items-center gap-3">
                          <cmd.icon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{cmd.label}</span>
                        </div>
                        <HotkeyRecorderCell
                          value={resolveHotkey(cmd.id, bindings)}
                          onRecord={(chord) => recordHotkey(cmd.id, chord)}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
